import type {
    ComponentProps,
    HTMLAttributes,
    ReactNode,
} from 'react'

import {
    Cell as AriaCell,
    Column as AriaColumn,
    Row as AriaRow,
    Table as AriaTable,
    TableBody as AriaTableBody,
    TableHeader as AriaTableHeader,
    type TableBodyProps as AriaTableBodyProps,
} from 'react-aria-components/Table'

import {
    /*
     * A bare `CheckboxButton` cannot stand alone: it reads its
     * render state from an internal context that only a
     * `CheckboxField` (or the deprecated combined `Checkbox`)
     * ancestor provides, and it never calls `useContextProps`
     * itself, so it cannot pick up the table's `slot="selection"`
     * wiring on its own.
     *
     * `CheckboxField` does call `useContextProps` against
     * `CheckboxFieldContext`, which react-aria's `<Row>` and
     * `<TableHeaderRow>` populate with
     * `slots: { selection: checkboxProps }` (verified against
     * react-aria-components 1.20.0, dist/private/Table.mjs).
     * `CheckboxField` renders the interactive `CheckboxButton` as
     * its child, and `CheckboxButtonRenderProps` extends
     * `CheckboxRenderProps`, so `CheckboxButton` alone still exposes
     * isSelected/isIndeterminate/isFocusVisible together to both its
     * `className` and `children` render props. So: `slot="selection"`
     * and `aria-label` go on `CheckboxField`, and all visual styling
     * goes on the nested `CheckboxButton`.
     */
    CheckboxButton as AriaCheckboxButton,
    CheckboxField as AriaCheckboxField,
} from 'react-aria-components'

import {
    Check,
    ChevronDown,
    ChevronUp,
    ChevronsUpDown,
    Minus,
} from 'lucide-react'

import { cn } from '@/shared/lib/cn'

export type DataTableStickyColumn =
    | 'none'
    | 'action'
    | 'key'

/**
 * Left offset of the frozen key column: after a 3.5rem action
 * column, or at the leading edge when there is no action column.
 */
export type DataTableKeyOffset =
    | 'none'
    | 'afterAction'

const KEY_STICKY_CLASSES = [
    'w-40 min-w-40',
    'max-w-56',
    'sm:w-48 sm:min-w-48',
    'sm:max-w-72',

    /*
     * Shows where the frozen columns end.
     */
    'shadow-[var(--shadow-sticky-column)]',
]

export interface DataTableScrollAreaProps
    extends HTMLAttributes<HTMLDivElement> {
    accessibleLabel?: string
}

export function DataTableScrollArea({
    accessibleLabel,
    className,
    children,
    ...props
}: DataTableScrollAreaProps) {
    return (
        <div
            {...props}
            role={
                accessibleLabel
                    ? 'region'
                    : undefined
            }
            aria-label={
                accessibleLabel
            }
            tabIndex={
                accessibleLabel
                    ? 0
                    : undefined
            }
            className={cn(
                [
                    'data-table-scroll-area relative isolate min-w-0 max-w-full',
                    'overflow-x-auto',
                    'rounded-lg',
                    'border border-(--color-border)',
                    'bg-(--color-surface)',
                    'shadow-[var(--shadow-card)]',

                    /*
                     * Shows a visible keyboard focus when the
                     * scroll region itself receives focus.
                     */
                    'outline-none',
                    'focus-visible:ring-2',
                    'focus-visible:ring-(--color-primary)',
                    'focus-visible:ring-offset-2',
                ],
                className,
            )}
        >
            {children}
        </div>
    )
}

export interface StyledTableProps
    extends Omit<
        ComponentProps<
            typeof AriaTable
        >,
        'className'
    > {
    className?: string
    minimumWidth?: string
}

export function StyledTable({
    className,
    minimumWidth = '56rem',
    style,
    ...props
}: StyledTableProps) {
    return (
        <AriaTable
            {...props}
            style={{
                minWidth: minimumWidth,
                ...style,
            }}
            className={cn(
                [
                    'w-full',
                    'border-separate',
                    'border-spacing-0',
                    'text-left',
                    'text-sm',
                    'text-(--color-foreground)',
                    'outline-none',
                ],
                className,
            )}
        />
    )
}

export interface StyledTableHeaderProps
    extends Omit<
        ComponentProps<
            typeof AriaTableHeader
        >,
        'className'
    > {
    className?: string
}

export function StyledTableHeader({
    className,
    ...props
}: StyledTableHeaderProps) {
    return (
        <AriaTableHeader
            {...props}
            className={cn(
                className,
            )}
        />
    )
}

export interface StyledColumnProps
    extends Omit<
        ComponentProps<
            typeof AriaColumn
        >,
        'className'
    > {
    sticky?: DataTableStickyColumn
    keyOffset?: DataTableKeyOffset
    className?: string
}

export function StyledColumn({
    sticky = 'none',
    keyOffset = 'afterAction',
    className,
    ...props
}: StyledColumnProps) {
    return (
        <AriaColumn
            {...props}
            className={cn(
                [
                    'sticky top-0 z-20',
                    'h-11 whitespace-nowrap',
                    'border-b border-(--color-border-strong)',
                    'bg-(--color-table-header)',
                    'px-4',
                    'text-xs font-semibold',
                    'tracking-[0.025em]',
                    'text-(--color-muted-foreground)',

                    'outline-none',

                    'data-focus-visible:ring-2',
                    'data-focus-visible:ring-inset',
                    'data-focus-visible:ring-(--color-primary)',

                    'data-allows-sorting:cursor-pointer',
                    'data-hovered:text-(--color-foreground)',
                    'data-hovered:bg-(--color-surface-muted)',
                ],

                sticky === 'action' && [
                    /*
                     * 3.5rem = Tailwind w-14.
                     * This must remain fixed because the key
                     * column begins after it.
                     */
                    'left-0 z-40',
                    'w-14 min-w-14 max-w-14',
                    'px-2 text-center',
                ],

                sticky === 'key' && [
                    keyOffset === 'afterAction'
                        ? 'left-14'
                        : 'left-0',
                    'z-40',
                    KEY_STICKY_CLASSES,
                ],

                className,
            )}
        />
    )
}

export type StyledTableBodyProps<
    T extends object,
> = Omit<
    AriaTableBodyProps<T>,
    'className'
> & {
    className?: string
}

export function StyledTableBody<
    T extends object,
>({
    className,
    ...props
}: StyledTableBodyProps<T>) {
    return (
        <AriaTableBody<T>
            {...props}
            className={cn(className)}
        />
    )
}

export interface StyledRowProps
    extends Omit<
        ComponentProps<
            typeof AriaRow
        >,
        'className'
    > {
    className?: string
}

export function StyledRow({
    className,
    ...props
}: StyledRowProps) {
    return (
        <AriaRow
            {...props}
            className={cn(
                [
                    'group',

                    'outline-none',

                    'data-focus-visible:ring-2',
                    'data-focus-visible:ring-inset',
                    'data-focus-visible:ring-(--color-primary)',

                    'data-disabled:opacity-50',
                ],
                className,
            )}
        />
    )
}

export interface StyledCellProps
    extends Omit<
        ComponentProps<
            typeof AriaCell
        >,
        'className'
    > {
    sticky?: DataTableStickyColumn
    keyOffset?: DataTableKeyOffset
    className?: string
}

export function StyledCell({
    sticky = 'none',
    keyOffset = 'afterAction',
    className,
    ...props
}: StyledCellProps) {
    return (
        <AriaCell
            {...props}
            className={cn(
                [
                    'h-13',
                    'border-b border-(--color-border)',
                    'bg-(--color-surface)',
                    'px-4 py-2',
                    'align-middle',

                    'transition-colors',

                    'group-data-hovered:bg-(--color-table-row-hover)',
                    'group-data-selected:bg-(--color-table-row-selected)',

                    'outline-none',

                    'data-focus-visible:ring-2',
                    'data-focus-visible:ring-inset',
                    'data-focus-visible:ring-(--color-primary)',
                ],

                sticky === 'action' && [
                    'sticky left-0 z-10',
                    'w-14 min-w-14 max-w-14',
                    'px-2 text-center',
                ],

                sticky === 'key' && [
                    'sticky z-10',
                    keyOffset === 'afterAction'
                        ? 'left-14'
                        : 'left-0',
                    KEY_STICKY_CLASSES,
                ],

                className,
            )}
        />
    )
}

export interface SortableColumnLabelProps {
    children: ReactNode

    sortDirection?:
        | 'ascending'
        | 'descending'

    align?: 'start' | 'end'
}

export function SortableColumnLabel({
    children,
    sortDirection,
    align = 'start',
}: SortableColumnLabelProps) {
    return (
        <span
            className={cn(
                'flex w-full items-center gap-1.5',

                align === 'end'
                    ? 'justify-end'
                    : 'justify-start',
            )}
        >
            <span>{children}</span>

            {sortDirection ===
            'ascending' ? (
                <ChevronUp
                    aria-hidden="true"
                    className="h-3.5 w-3.5"
                />
            ) : sortDirection ===
              'descending' ? (
                <ChevronDown
                    aria-hidden="true"
                    className="h-3.5 w-3.5"
                />
            ) : (
                <ChevronsUpDown
                    aria-hidden="true"
                    className="h-3.5 w-3.5 opacity-50"
                />
            )}
        </span>
    )
}

export interface SelectionCheckboxProps {
    /**
     * Only the select-all header needs an explicit label. Bare row
     * checkboxes rely on the `aria-labelledby` that react-aria's
     * `selection` slot already supplies (pointing at the row's
     * key cell), so they're left unset.
     */
    'aria-label'?: string
}

/**
 * Bare selection checkbox for table rows and the select-all header.
 * Styled to match the shared Checkbox component's box. react-aria
 * wires slot="selection" to the table's selection state, including
 * the indeterminate select-all state.
 */
export function SelectionCheckbox({
    'aria-label': ariaLabel,
}: SelectionCheckboxProps) {
    return (
        <AriaCheckboxField
            slot="selection"
            aria-label={ariaLabel}
            className="contents"
        >
            <AriaCheckboxButton
                className={({
                    isFocusVisible,
                }) =>
                    cn(
                        'inline-flex items-center justify-center rounded-md p-1 outline-none',
                        isFocusVisible && 'ring-2 ring-(--color-primary) ring-offset-2 ring-offset-(--color-surface)',
                    )
                }
            >
                {({
                    isSelected,
                    isIndeterminate,
                }) => (
                    <span
                        className={cn(
                            [
                                'flex h-5 w-5 shrink-0',
                                'items-center justify-center',
                                'rounded border',
                                'transition-colors',
                            ],
                            isSelected || isIndeterminate
                                ? [
                                      'border-(--color-primary)',
                                      'bg-(--color-primary)',
                                      'text-white',
                                  ]
                                : [
                                      'border-(--color-border)',
                                      'bg-(--color-surface)',
                                  ],
                        )}
                    >
                        {isIndeterminate ? (
                            <Minus
                                aria-hidden="true"
                                className="h-3.5 w-3.5"
                                strokeWidth={3}
                            />
                        ) : isSelected ? (
                            <Check
                                aria-hidden="true"
                                className="h-3.5 w-3.5"
                                strokeWidth={3}
                            />
                        ) : null}
                    </span>
                )}
            </AriaCheckboxButton>
        </AriaCheckboxField>
    )
}
